import { availableFromText } from "@/components/vendor/ServiceAvailableFrom";
import React, { useEffect, useState } from "react";
import ImageSlider, { photoList } from "@/components/ImageSlider";
import { useParams, Link } from "react-router-dom";
import { fetchService, resolveImageUrl, serviceReviewApi } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { contactGate } from "@/lib/contactGate";
import ShopStatusPill from "@/components/ShopStatusPill";
import { ServiceCard } from "@/components/Cards";
import { MapPin, Star, Wrench, Phone, Clock, Tag, ShieldCheck, MessageCircle, Store, UserRound, Share2 } from "lucide-react";
import ServiceShareSheet, { isShareableService } from "@/components/ServiceShareSheet";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";
import ReviewsSection from "@/components/ReviewsSection";
import MapPreview from "@/components/maps/MapPreview";
import { waUrl } from "@/lib/whatsapp";
import { serviceEnquiryMsg } from "@/lib/enquiry";
import { ShareLocationCard } from "@/components/maps/LocationShare";

export default function ServiceDetail() {
  const { id } = useParams();
  const { pincode, user } = useApp();
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchService(id, pincode).then(setSvc).catch(() => {}).finally(() => setLoading(false));
  }, [id, pincode]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="service-loading">Loading...</div>;
  if (!svc) return <div className="max-w-4xl mx-auto px-4 py-16 text-center">Service not found</div>;

  const waDigits = String(svc.contact_whatsapp || svc.shop_whatsapp || svc.contact_phone || "").replace(/\D/g, "");
  const waNumber = waDigits.length === 10 ? `91${waDigits}` : waDigits.length >= 11 ? waDigits : "";
  const phoneDigits = String(svc.contact_phone || "").replace(/\D/g, "");
  const contact = () => {
    if (contactGate(user, svc.contact_locked)) return;
    if (phoneDigits.length < 10) return toast.info("Is service provider ne abhi contact number listed nahi kiya hai.");
    window.location.href = `tel:${phoneDigits}`;
  };
  const providerName = svc.provider_name || svc.provider || svc.shop_name || "";

  return (
    <div data-testid="service-detail-page">
      {svc.is_owner && (svc.active === false || svc.review_status === "pending") && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm px-4 py-2 text-center" data-testid="service-hidden-banner">
          Ye service abhi {svc.active === false ? "Inactive (hidden)" : "Pending approval"} hai — sirf aap ise dekh sakte hain.
        </div>
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <BackButton fallback="/category/services" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-4" testid="service-back-btn" />

        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-6 lg:gap-10">
          <div className="rounded-3xl overflow-hidden border border-slate-100 bg-slate-50 aspect-[4/3]">
            {photoList(svc).length ? <ImageSlider images={photoList(svc)} captions={svc.image_captions} alt={svc.name} className="w-full h-full" testid="service-slider" /> : <img src={resolveImageUrl("")} alt={svc.name} className="w-full h-full object-cover" />}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-2">
              <Wrench className="w-3.5 h-3.5" /> Local service
              {svc.shop_status && <ShopStatusPill status={svc.shop_status} shopId={svc.shop_id} size="lg" />}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900" data-testid="service-title">{svc.name}</h1>
            {svc.available === false && <span className="inline-block mt-2 bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" data-testid="service-detail-unavailable">Unavailable</span>}
            {availableFromText(svc) && <span className="inline-block mt-2 ms-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="service-detail-available-from">{availableFromText(svc)}</span>}
            {svc.hindi && <div className="text-sm text-slate-500 mt-1">{svc.hindi}</div>}
            {providerName && (
              <div className="mt-2 flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-0.5 min-w-0" data-testid="service-provider-block">
                <UserRound className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                <span className="text-xs text-slate-500 flex-shrink-0">Service provider:</span>
                {svc.shop_slug || svc.profile_id ? (
                  <Link to={svc.shop_slug ? `/v/${svc.shop_slug}` : `/p/${svc.profile_id}`} className="text-base font-bold text-[rgb(var(--vb-primary-dark))] hover:underline underline-offset-4 truncate min-w-0 max-w-full" data-testid="service-provider-name">{providerName}</Link>
                ) : (
                  <span className="text-base font-bold text-slate-900 truncate min-w-0 max-w-full" data-testid="service-provider-name">{providerName}</span>
                )}
                {svc.shop_name && svc.shop_name.trim() !== providerName.trim() ? <span className="text-sm text-slate-500 truncate min-w-0 max-sm:basis-full max-sm:pl-6">· {svc.shop_name}</span> : null}
              </div>
            )}

            <div className="mt-3 flex items-center gap-4 text-sm text-slate-600 flex-wrap">
              <div className="flex items-center gap-1 bg-amber-50 text-amber-800 px-2 py-1 rounded font-semibold">
                <Star className="w-3.5 h-3.5 fill-current" /> {!svc.reviews && svc.review_count ? svc.review_avg : svc.rating || "-"}
              </div>
              {svc.reviews ? <span>{svc.reviews} reviews</span> : null}
              {svc.distance_km !== null && svc.distance_km !== undefined && (
                <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {svc.distance_km} km · {svc.location}</div>
              )}
            </div>

            <div className="mt-5 bg-white border border-slate-100 rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{svc.price_from == null ? "Fee" : "Starts from"}</div>
              <div className="font-display text-3xl font-bold text-slate-900" data-testid="service-price">{svc.price_from == null ? "As discussed" : `₹${svc.price_from}`}</div>
              <div className="text-xs text-slate-500">{svc.price_from == null ? "Custom quotation on enquiry" : svc.unit}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={contact} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 text-sm font-semibold" data-testid="service-contact-btn">
                  <Phone className="w-4 h-4" /> Contact
                </button>
                {waNumber || svc.contact_locked ? (
                  <a href={waNumber ? waUrl(waNumber, serviceEnquiryMsg(providerName, svc.name)) : undefined} onClick={(e) => { if (contactGate(user, svc.contact_locked)) e.preventDefault(); }} role="button" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white px-5 py-2.5 text-sm font-semibold" data-testid="service-whatsapp-enquiry">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Enquiry
                  </a>
                ) : (
                  <span className="basis-full text-xs text-slate-500" data-testid="service-no-whatsapp">WhatsApp number listed nahi hai — provider ne abhi WhatsApp contact publish nahi kiya.</span>
                )}
                {svc.shop_slug ? (
                  <Link to={`/v/${svc.shop_slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 text-slate-800 px-5 py-2.5 text-sm font-semibold" data-testid="service-shop-btn">
                    <Store className="w-4 h-4" /> Visit shop
                  </Link>
                ) : svc.profile_id ? (
                  <Link to={`/p/${svc.profile_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 text-slate-800 px-5 py-2.5 text-sm font-semibold" data-testid="service-profile-btn">
                    <UserRound className="w-4 h-4" /> View Profile
                  </Link>
                ) : null}
                {isShareableService(svc) && (
                  <button type="button" onClick={() => setShareOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 bg-white hover:bg-emerald-50 px-5 py-2.5 text-sm font-semibold" data-testid="service-share-btn">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                )}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 min-[360px]:grid-cols-2 gap-3 text-sm">
              {svc.category && <InfoRow label="Category" value={svc.category} icon={Tag} />}
              {svc.subcategory && <InfoRow label="Sub-category" value={svc.subcategory} icon={Tag} />}
              {svc.duration && <InfoRow label="Duration" value={svc.duration} icon={Clock} />}
              {svc.experience_years ? <InfoRow label="Experience" value={`${svc.experience_years}+ yrs`} icon={ShieldCheck} /> : null}
              <InfoRow label="Provider" value={providerName || "-"} icon={Wrench} />
              {svc.qualification?.trim() && <InfoRow label="Qualification" value={svc.qualification} icon={ShieldCheck} />}
              {svc.specialties?.trim() && <InfoRow label="Specialties" value={svc.specialties} icon={Tag} />}
              {svc.service_pincodes?.length ? (
                <InfoRow label="Serves pincodes" value={svc.service_pincodes.join(", ")} icon={MapPin} />
              ) : (svc.pincode && <InfoRow label="Pincode" value={svc.pincode} icon={MapPin} />)}
            </div>
          </div>
        </div>

        {svc.description && (
          <div className="mt-8 bg-white border border-slate-100 rounded-2xl p-5">
            <h3 className="font-display text-lg font-bold mb-2">About this service</h3>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{svc.description}</p>
          </div>
        )}

        <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="service-location-section">
          <MapPreview embedUrl={svc.map_embed_url} viewUrl={svc.map_view_url} directionsUrl={svc.directions_url} approx={!!svc.map_approx} noDirectionsText="Provider ne exact location share nahi ki — directions ke liye Call/WhatsApp karein." emptyText="Provider ki exact map location saved nahi hai — sahi pata Call/WhatsApp karke poochein." testid="service-map" />
          <ShareLocationCard serviceId={svc.id} isOwner={!!svc.is_owner} />
          <ServiceShareSheet open={shareOpen} onClose={() => setShareOpen(false)} svc={svc} />
        </div>

        <ReviewsSection key={svc.id} slug={svc.id} api={serviceReviewApi(svc.id)} isOwner={!!svc.is_owner} shopName={providerName} selfText="Aap apni khud ki service ka review nahi de sakte." testid="service-reviews-section" />

        {svc.related?.length > 0 && (
          <div className="mt-10">
            <h3 className="font-display text-xl font-bold mb-4">Related services</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
              {svc.related.map((r) => <ServiceCard key={r.id} service={r} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl p-3 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Icon className="w-3 h-3 flex-shrink-0" /> {label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5 break-words">{value}</div>
    </div>
  );
}
